"use client";

import HomepageBanner from "./homepageBanner";
import "react-loading-skeleton/dist/skeleton.css";
import Modal from "../../components/Modal/modal";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { publishModalVisibility } from "../../redux/features/modal/modalSlice";
import { lazy, useEffect, useState } from "react";
import { ToastProvider } from "../../components/ToastNotification/toastProvider";
import { getUmbracoGrid } from "../../common-service";
import { GridProps } from "../../common";
import { HomepageMacros } from "./homepageMacros";
import "./home.css";
import CategoryMenu from "./categoryMenu";
import { useCompetitionsCategoriesLoading } from "../../redux/features/categories/selectors";
import HomeCompetitionsLoader from "./homepageCompetitions/homeCompetitionsLoader";
import { setIsUserASubscriber, setSubscriptionLobbyContent } from "../../redux/features/header/headerSlice";
import { getCookie } from "../../../helpers/cookies";
import { getSubscriptionLobbyContent, getUserSubscriptionPlan } from "../subscription/subscription-service";
import { hasActiveSubscription } from "../../../helpers/hasActiveSubscription";
import LazyOnVisible from "@/app/shared-components/LazyOnVisible";
import BotbPassCancelledModal from "./BotbPassCancelledModal";
import useDeferredReviveAds from "@/app/hooks/useDeferredReviveAds";

// Below-the-fold sections: split into their own webpack chunks and mount only when scrolled
// near, so their code (embla-carousel/auto-scroll, DOMPurify) + API calls stay off the initial
// home.js bootup path. See LazyOnVisible for the viewport gating.
const WinnersFeedWidget = lazy(() => import("./winnersFeed/WinnersFeedWidget"));
const CarOfTheWeek = lazy(() => import("./carOfTheWeek/carOfTheWeek"));

const Home = () => {
  const buyYourTicketsModalData = useAppSelector(state => state.modal.data);
  const isBuyTicketModalVisible = useAppSelector(state => state.modal.isOpen);
  const dispatch = useAppDispatch();
  const [data, setData] = useState<GridProps>({
    botbGridContent: {},
  });
  const isUserASubscriber = useAppSelector(state => state.header.isUserASubscriber);
  const [cancelledPassModal, setCancelledPassModal] = useState<{
    tickets: number;
    competitions: number;
  } | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("botbPassCancelled") === "1") {
      setCancelledPassModal({
        tickets: Number(params.get("tickets") || 0),
        competitions: Number(params.get("competitions") || 0),
      });
      params.delete("botbPassCancelled");
      params.delete("tickets");
      params.delete("competitions");
      const nextSearch = params.toString();
      const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}${window.location.hash}`;
      window.history.replaceState({}, "", nextUrl);
    }
  }, []);

  useEffect(() => {
    const isAuth = getCookie("BotB.Authenticated") === "true";
    if (isAuth) {
      getUserSubscriptionPlan()
        .then(res => res.json())
        .then(res => {
          dispatch(setIsUserASubscriber(!!res));
        })
        .catch(err => {
          dispatch(setIsUserASubscriber(false));
        });
    }
  }, []);

  useEffect(() => {
    if (isUserASubscriber) {
      const abortController = new AbortController();
      getSubscriptionLobbyContent(abortController)
        .then(res => {
          dispatch(setSubscriptionLobbyContent(res));
        })
        .catch(err => console.error(err));
      return () => abortController.abort();
    }
  }, [isUserASubscriber]);

  useEffect(() => {
    (async () => {
      try {
        const response = await getUmbracoGrid();
        const homepageMacroData = (await response.json()) as GridProps;
        setData(homepageMacroData);
      } catch (err) {
        console.error(`Error while fetching the homepage grid - ${JSON.stringify(err)}`);
      }
    })();
  }, []);

  useEffect(() => {
    if (performance.navigation.type === performance.navigation.TYPE_RELOAD) {
      const previousScrollTop = sessionStorage.getItem("pageScrollY");
      if (previousScrollTop) {
        setTimeout(
          () =>
            window.scrollTo({
              top: Number(previousScrollTop),
              behavior: "auto",
            }),
          500
        );
      }
    } else {
      sessionStorage.removeItem("pageScrollY");
    }
    window.addEventListener("beforeunload", handleScroll);
    return () => window.removeEventListener("beforeunload", handleScroll);
  }, []);

  // Revive ads (~1.3 MB of animated GIF banners) load after window load + idle so they do not
  // compete with the LCP hero and competition images. Same pattern as winners/lifestyle pages.
  useDeferredReviveAds();

  const handleScroll = () => {
    sessionStorage.setItem("pageScrollY", window.scrollY.toString());
  };

  const areCompetitionCategoriesLoading = useCompetitionsCategoriesLoading();
  const isLoading = areCompetitionCategoriesLoading;

  return (
    <div className="bg-[#EFEFEF] botb-homepage">
      <HomepageBanner />
      <CategoryMenu />

      <LazyOnVisible rootMargin="800px">
        <WinnersFeedWidget />
      </LazyOnVisible>

      <div id={"script_ad_server"} className={"hidden"} />

      <div id="revive-ad-container-mobile" className={"lg:hidden"}>
        {" "}
        <ins
          data-revive-usersubscriber={hasActiveSubscription()}
          data-revive-zoneid="2"
          data-revive-id="6ee315d200a003a5ac39b07d0dd34156"
          className={
            "mx-auto mt-5 lg:mt-10 mb-6 lg:mb-10 max-w-[1360px] xs:px-2 md:px-5 relative [&_img]:rounded-lg block [&:empty]:hidden"
          }></ins>
      </div>

      <div id="revive-ad-container-desktop" className={"max-lg:hidden"}>
        {" "}
        <ins
          data-revive-usersubscriber={hasActiveSubscription()}
          data-revive-zoneid="1"
          data-revive-id="6ee315d200a003a5ac39b07d0dd34156"
          className={
            "mx-auto mt-5 lg:mt-10 mb-6 lg:mb-10 max-w-[1360px] xs:px-2 md:px-5 relative [&_img]:rounded-lg block [&:empty]:hidden"
          }></ins>
      </div>

      {isLoading ? <HomeCompetitionsLoader /> : <HomepageMacros botbGridContent={data?.botbGridContent} />}
      <LazyOnVisible minHeight={520}>
        <CarOfTheWeek />
      </LazyOnVisible>
      <Modal
        isOpen={isBuyTicketModalVisible}
        title=""
        closeModal={event => {
          event.stopPropagation();
          dispatch(publishModalVisibility({ isOpen: false }));
        }}
        modalClassNames={`md:rounded-2xl xs:absolute xs:bottom-0 xs:w-full xs:rounded-b-none xs:rounded-t-2xl z-10 !max-h-none ${isBuyTicketModalVisible ? "!px-4 pt-12 pb-8 max-lg:!pb-4 lg:!px-8 lg:!pb-10 lg:!pt-12" : ""}`}
        body={buyYourTicketsModalData}
      />
      {cancelledPassModal && (
        <BotbPassCancelledModal
          ticketCount={cancelledPassModal.tickets}
          competitionCount={cancelledPassModal.competitions}
          onClose={() => setCancelledPassModal(null)}
        />
      )}
      <ToastProvider />
    </div>
  );
};

export default Home;
